import { createContext, useContext, useEffect, useState } from "react";
import api from "./api";

const SettingsCtx = createContext({ settings: {}, loading: true });

const DEFAULTS = {
  app_name: "AdventureCouple.lu – Wildpflanzen Luxemburg",
  contact_email: "natur@adventurecouple.lu",
  hero_title: "Die wilde Speisekammer\nLuxemburgs.",
  hero_subtitle: "Lerne essbare Pflanzen aus Wald, Wiese und Hecke kennen.",
  hero_overline: "Wildpflanzen · Luxemburg · Großregion",
  hero_image_url: "/fotos/sonnenwald.webp",
  hero_cta_primary: "Pflanzendatenbank",
  hero_cta_secondary: "Pflanze erkennen",
  quote_text: "\u201eDie Natur kennt den Weg.\nWir m\u00fcssen nur lauschen.\u201c",
  recognize_title: "Lade ein Foto hoch – wir sagen dir, was da wächst.",
  recognize_text: "Unsere Pflanzenerkennung nutzt die Pl@ntNet-API.",
  recognize_image_url: "https://images.unsplash.com/photo-1663153206192-6d0e4c9570dd?w=1200&q=80",
  tours_title: "Komm mit uns raus.",
  tours_text: "Wildkräuter sicher erkennen, den Wald im Wandel verstehen oder fremden Arten vor unserer Haustür auf die Spur kommen. Kostenlos, für Erwachsene, in kleinen Gruppen bis 15 Personen – keine Vorkenntnisse nötig.",
  tours_image_url: "https://images.unsplash.com/photo-1597120590849-a1d5a743d155?w=1200&q=80",
  safety_title: "Sicher sammeln – immer 100 % bestimmen.",
  safety_text: "Nur ernten, was du eindeutig erkennst. Im Zweifel KI-Erkennung nutzen oder bei einer geführten Tour mitkommen. Giftnotruf Luxemburg: 8002 5500 (Centre Antipoisons, 24/7, kostenlos). Bei Bewusstlosigkeit, Krampfanfall oder Atemnot sofort 112.",
  newsletter_title: "Pflanze der Woche per Mail.",
  newsletter_text: "Jeden Montag eine luxemburgische Wildpflanze.",
  footer_tagline: "Natur erleben. Wissen teilen. Bewusst unterwegs.",
  footer_copyright: "© AdventureCouple.lu – Alle Rechte vorbehalten.",
  footer_motto: "Sammle achtsam – nimm nur, was du brauchst. 🌿",
  footer_quote: "\u201eDie Natur kennt den Weg.\nWir m\u00fcssen nur lauschen.\u201c",
  footer_social_instagram: "https://www.instagram.com",
  footer_social_facebook: "https://www.facebook.com",
  footer_social_youtube: "https://www.youtube.com",
  footer_location: "Luxemburg, Großregion",
  header_login_label: "Login",
  header_logo_url: "",
  footer_logo_url: "",
  home_tagline_line1: "Natur erleben.",
  home_tagline_line2: "Wissen teilen.",
  home_tagline_line3: "Bewusst unterwegs.",
  home_tile1_label: "119+ Pflanzen",
  home_tile1_sub: "essbar · heilend · giftig",
  home_tile2_label: "KI-Erkennung",
  home_tile2_sub: "Foto hochladen",
  home_tile3_label: "Geführte Touren",
  home_tile3_sub: "kostenlos, für Erwachsene",
  home_tile4_label: "Saisonkalender",
  home_tile4_sub: "Monat für Monat",
  maintenance_mode: false,
  maintenance_message: "Wir sind kurz mit den Wildkräutern unterwegs – die Seite ist gerade in Wartung. Bald wieder da! 🌿",
  maintenance_logo_url: "",
  maintenance_title: "Kurz in der Natur",
  maintenance_status: "Schau gleich nochmal vorbei",
  maintenance_quote: "\u201eDie Natur kennt den Weg.\nWir m\u00fcssen nur lauschen.\u201c",
  embed_brand_color: "#2D5A27",
  embed_powered_by: "",
  embed_book_label: "Buchen",
  embed_loading_label: "Lade Touren…",
  embed_empty_title: "Aktuell keine Touren geplant",
  embed_empty_text: "Wir bereiten gerade neue Wildkräuter-Wanderungen vor. Schau bald wieder vorbei oder besuche unsere Website.",
  embed_empty_cta_label: "→ Website besuchen",
  embed_free_label: "Gratis",
  embed_sold_out_label: "Ausgebucht",
  embed_waitlist_label: "Auf Warteliste",
  cookie_banner_enabled: true,
  cookie_banner_text: "Wir verwenden nur technisch notwendige Cookies (z. B. für deinen Login). Keine Tracker, kein Verkauf deiner Daten. Mehr in unserer Datenschutzerklärung.",
  cookie_banner_accept_label: "Verstanden",
  cookie_banner_privacy_link_label: "Datenschutz",
  cookie_banner_privacy_link_url: "/impressum",
  theme_sand: "#CBBCA5",
  theme_cream: "#E7DED0",
  theme_sage: "#8C8A6A",
  theme_olive: "#5F5B3E",
  theme_forest: "#3A392C",
  theme_text_muted: "#6E6A55",
  theme_toxic: "#A73A3A",
  theme_medicinal: "#C9A36E",
  theme_border: "#D5C9B4",
  theme_button_radius: "9999px",
  theme_font_scale: 1.0,
  theme_active_preset: "default",
};

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/settings")
      .then((r) => setSettings({ ...DEFAULTS, ...(r.data || {}) }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Inject theme variables into :root whenever settings change.
  // Lets the admin change brand colors / button radius / font scale and see it everywhere instantly.
  useEffect(() => {
    const root = document.documentElement;
    const map = {
      "--wp-sand": settings.theme_sand,
      "--wp-cream": settings.theme_cream,
      "--wp-sage": settings.theme_sage,
      "--wp-olive": settings.theme_olive,
      "--wp-forest": settings.theme_forest,
      "--wp-text-muted": settings.theme_text_muted,
      "--wp-toxic": settings.theme_toxic,
      "--wp-medicinal": settings.theme_medicinal,
      "--wp-border": settings.theme_border,
      "--wp-button-radius": settings.theme_button_radius,
    };
    for (const [k, v] of Object.entries(map)) {
      if (v) root.style.setProperty(k, String(v));
    }
    // Font scale via root font-size (rem)
    const scale = Number(settings.theme_font_scale) || 1;
    root.style.fontSize = `${Math.max(0.75, Math.min(1.4, scale)) * 16}px`;
  }, [settings.theme_sand, settings.theme_cream, settings.theme_sage, settings.theme_olive, settings.theme_forest, settings.theme_text_muted, settings.theme_toxic, settings.theme_medicinal, settings.theme_border, settings.theme_button_radius, settings.theme_font_scale]);

  return <SettingsCtx.Provider value={{ settings, loading }}>{children}</SettingsCtx.Provider>;
}

export const useSettings = () => useContext(SettingsCtx).settings;
